import { useState, useEffect, useCallback, useRef } from "react";
import { Login } from "@/models/auth.model";
import apiClient from "../api/apiClient";
import { useToast } from "./use-toast";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "@/app/hooks";
import { clearLeaveRequests } from "@/features/leaveRequests/leaveRequestSlice";

export const useAuth = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [accessToken, setAccessToken] = useState<string | null>(() => {
    return sessionStorage.getItem("accessToken");
  });

  const [refreshSubscribers, setRefreshSubscribers] = useState<
    Array<(token: string) => void>
  >([]);
  const isRefreshingRef = useRef(false);

  const updateAccessToken = useCallback((token: string | null) => {
    console.log("Updating session token...");
    if (token) {
      sessionStorage.setItem("accessToken", token);
    } else {
      sessionStorage.removeItem("accessToken");
    }
    setAccessToken(token);
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await apiClient.post("/auth/login", { email, password });
      const data: Login = response.data;
      console.log("Login successful:", data);
      updateAccessToken(data.accessToken);

      return {
        user: data.user,
        isNewUser: data.isNewUser,
      };
    } catch (error: any) {
      console.error("Login failed:", error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await apiClient.post(
        "/auth/logout",
        {},
        { withCredentials: true, headers: { "Skip-Interceptor": "true" } }
      );
      updateAccessToken(null); // Clear session token
      dispatch(clearLeaveRequests())

      toast({
        title: "Logout successful!",
        description: `You have been logged out`,
      });
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const refreshAccessToken = useCallback(async () => {
    console.log("REFRESH TOKEN FUNCTION CALLED");

    if (isRefreshingRef.current) {
      console.log("Already refreshing, adding to subscribers");
      return new Promise<string>((resolve) => {
        setRefreshSubscribers((subscribers) => [...subscribers, resolve]);
      });
    }

    isRefreshingRef.current = true;

    try {
      console.log("Making refresh request");
      // Important: Don't use Authorization header for refresh
      const refreshConfig = {
        withCredentials: true,
        headers: {
          "Skip-Interceptor": "true",
          // Delete the Authorization header for this request
          "Authorization": undefined
        }
      };

      const response = await apiClient.post(
        "/auth/refresh",
        {},
        refreshConfig
      );

      console.log("Refresh response:", response.data);
      const newToken = response.data.accessToken;

      updateAccessToken(newToken);
      refreshSubscribers.forEach((callback) => callback(newToken));
      setRefreshSubscribers([]);

      return newToken;
    } catch (error: any) {
      console.error("Token refresh failed:", error.message);
      console.error("Response status:", error.response?.status);
      console.error("Response data:", error.response?.data);
      updateAccessToken(null);
      throw error;
    } finally {
      isRefreshingRef.current = false;
    }
  }, [updateAccessToken, refreshSubscribers]);

  // Set up the interceptor for automatic token refresh
  useEffect(() => {
    console.log("Setting up auth interceptor");

    const interceptor = apiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        console.log("Response error:", error.response?.status);

        const originalRequest = error.config;

        // Skip interceptor logic under certain conditions
        if (!originalRequest ||
          originalRequest.headers["Skip-Interceptor"] ||
          originalRequest._retry) {
          return Promise.reject(error);
        }

        // Handle 401 Unauthorized errors
        if (error.response?.status === 401) {
          console.log("401 detected - triggering refresh token");
          originalRequest._retry = true;

          try {
            const newToken = await refreshAccessToken();
            console.log("New token obtained, retrying request");

            // Update the authorization header for the retried request
            originalRequest.headers["Authorization"] = `Bearer ${newToken}`;

            // Retry the original request with the new token
            return apiClient(originalRequest);
          } catch (refreshError) {
            console.error("Refresh failed, redirecting to login");
            updateAccessToken(null);
            navigate('/');
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );

    // Clean up the interceptor when the component unmounts
    return () => {
      apiClient.interceptors.response.eject(interceptor);
    };
  }, [refreshAccessToken, navigate, updateAccessToken]);

  return {
    accessToken,
    login,
    logout,
    refreshAccessToken,
  };
};